'use client';

import { isValidElement, ReactNode, useEffect, useRef, useState } from 'react';

import { Ban, CheckCircle2, Circle, CircleUserRound, Loader2, XCircle } from 'lucide-react';
import { useLatest } from 'react-use';

import type { ProgressStepStatus } from '@/components/assistant-ui/thread/trail-records';
import { cn } from '@/lib/utils';

function StatusGlyph({
  status,
  customInProgressIcon,
}: {
  status: ProgressStepStatus;
  customInProgressIcon?: ReactNode;
}) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-5 w-5 text-destructive" />;
  }
  if (status === 'cancelled') {
    // Ban (matches the card's "cancelled" alert), gray — a neutral stop, not an error.
    return <Ban className="h-5 w-5 text-gray-400 dark:text-gray-500" />;
  }
  if (status === 'awaiting-approval') {
    // Waiting on the USER, not on work — a neutral, deliberately non-spinning glyph.
    return <CircleUserRound className="h-5 w-5 text-primary" aria-hidden="true" />;
  }
  if (status === 'in-progress') {
    if (customInProgressIcon) return <>{customInProgressIcon}</>;
    return (
      <Loader2
        className="h-5 w-5 animate-spin text-primary motion-reduce:animate-none"
        aria-hidden="true"
      />
    );
  }
  return <Circle className="h-5 w-5 text-muted-foreground" />;
}

export const STATUS_TRANSITION_MS = 300;

/**
 * Crossfades between glyphs on `status` flip (⟳ → ✓, ⟳ → ✗), `iconKey`
 * change (row morph, e.g. trailing thinking row's spinner becoming a tool
 * record's rotating icon at the same React key), or when the in-progress icon
 * node's `key` changes (a step swapping its icon SET mid-status, e.g. the
 * contact step going fetch → awaiting-approval). Two-phase: fade-and-shrink,
 * swap, fade-and-grow.
 *
 * `customInProgressIcon` is a fresh ReactNode each render, so comparing it
 * would re-fire the effect every tick; we read the *latest* node from a ref
 * inside the swap timer, and gate the transition on `status` + `iconKey` +
 * the node's `key` (all stable strings). Concretely: a tick that only
 * re-renders the icon node (same key) won't restart the crossfade.
 */
export function StepStatusIcon({
  status,
  customInProgressIcon,
  iconKey,
}: {
  status: ProgressStepStatus;
  customInProgressIcon?: ReactNode;
  iconKey?: string;
}) {
  const iconNodeKey = isValidElement(customInProgressIcon) ? customInProgressIcon.key : null;
  const [visible, setVisible] = useState(() => ({
    status,
    iconKey,
    iconNodeKey,
    customInProgressIcon,
  }));
  const [isTransitioning, setIsTransitioning] = useState(false);
  const swapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read the latest icon node lazily when the timer fires so a fresh
  // ReactNode on every render doesn't force the effect to re-run; the
  // visible state still holds the OLD icon during the fade-out phase.
  const customInProgressIconRef = useLatest(customInProgressIcon);

  useEffect(() => {
    if (
      status === visible.status &&
      iconKey === visible.iconKey &&
      iconNodeKey === visible.iconNodeKey
    )
      return;
    // setState-in-effect is intentional: the CSS crossfade is driven by an
    // `isTransitioning` class flip on the wrapper, then a delayed `visible`
    // swap. There's no external system to subscribe to — the animation
    // timing IS the state machine.

    setIsTransitioning(true);
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
    swapTimerRef.current = setTimeout(() => {
      setVisible({
        status,
        iconKey,
        iconNodeKey,
        customInProgressIcon: customInProgressIconRef.current,
      });
      setIsTransitioning(false);
      swapTimerRef.current = null;
    }, STATUS_TRANSITION_MS);
    return () => {
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
        swapTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- customInProgressIconRef is a stable ref from useLatest
  }, [status, iconKey, iconNodeKey, visible.status, visible.iconKey, visible.iconNodeKey]);

  return (
    <span
      className={cn(
        'inline-flex transition-all duration-300 ease-in-out motion-reduce:transition-none',
        isTransitioning ? 'scale-50 opacity-0' : 'scale-100 opacity-100',
      )}
    >
      <StatusGlyph status={visible.status} customInProgressIcon={visible.customInProgressIcon} />
    </span>
  );
}
